import { useState } from 'react';
import { extractErrorMessage } from '../../api/errors';
import { Badge, Button, Callout, Card, CardContent, CardDescription, CardHeader, CardTitle, ConfirmDialog, Spinner } from '../../components/ui';
import { Enrolment, EnrolmentSend, useSeriesEnrolments, useStopEnrolment } from '../../queries/emailSeries';
import { formatCalendarDate, formatSeriesDate } from './emailSeriesFormat';

const PAGE_SIZE = 25;

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'converted', label: 'Converted' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'stopped', label: 'Stopped' },
];

const STATUS_BADGE: Record<string, 'default' | 'primary' | 'success' | 'warning' | 'destructive' | 'outline'> = {
  active: 'primary',
  completed: 'default',
  converted: 'success',
  unsubscribed: 'warning',
  stopped: 'outline',
};

const statusLabel = (status: string) => STATUS_FILTERS.find((f) => f.value === status)?.label ?? status;

function SendLine({ send }: { send: EnrolmentSend }) {
  const day = `Step ${send.stepOrder}`;
  switch (send.status) {
    case 'sent':
      return <li>{day}: sent {formatSeriesDate(send.sentAt)}</li>;
    case 'dry_run':
      return <li>{day}: dry run {formatSeriesDate(send.sentAt)} (not sent)</li>;
    case 'skipped':
      return <li className="text-amber-600 dark:text-amber-400">{day}: skipped{send.skippedReason ? ` (${send.skippedReason})` : ''}</li>;
    case 'failed':
      return <li className="text-destructive">{day}: failed{send.error ? ` (${send.error})` : ''}</li>;
    default:
      return <li>{day}: sending</li>;
  }
}

export function EmailSeriesEnrolments({ seriesId, canWrite }: { seriesId: number; canWrite: boolean }) {
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [removing, setRemoving] = useState<Enrolment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [codeWarning, setCodeWarning] = useState<string | null>(null);
  const { data, isLoading, error: loadError } = useSeriesEnrolments(seriesId, { status: status || undefined, limit: PAGE_SIZE, offset });
  const stop = useStopEnrolment();

  const total = data?.total ?? 0;
  const enrolments = data?.enrolments ?? [];

  const confirmRemove = () => {
    if (!removing) return;
    setError(null);
    setCodeWarning(null);
    const who = removing.name ?? removing.email ?? 'This customer';
    stop.mutate(removing.id, {
      onSuccess: (result) => {
        setRemoving(null);
        if (result?.hadCode && !result.codeDeactivated) {
          setCodeWarning(`${who} is out of the series, but their code could not be switched off in Stripe, so it still works until it expires.`);
        }
      },
      onError: (err) => {
        setRemoving(null);
        setError(extractErrorMessage(err, 'Could not remove them from the series.'));
      },
    });
  };

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <div className="space-y-1">
          <CardTitle>Enrolments</CardTitle>
          <CardDescription>Everyone this series has picked up, newest first.</CardDescription>
        </div>
        <div>
          <label htmlFor="enrolment-status" className="sr-only">Status</label>
          <select
            id="enrolment-status"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setOffset(0); }}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          >
            {STATUS_FILTERS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {error && <Callout variant="error">{error}</Callout>}
        {codeWarning && <Callout variant="warning">{codeWarning}</Callout>}
        {loadError && <Callout variant="error" title="Couldn't load enrolments">{extractErrorMessage(loadError)}</Callout>}
        {isLoading ? (
          <Spinner />
        ) : enrolments.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nobody enrolled{status ? ` with status ${statusLabel(status).toLowerCase()}` : ''} yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3 font-medium">Customer</th>
                  <th className="py-2 pr-3 font-medium">Trigger</th>
                  <th className="py-2 pr-3 font-medium">Status</th>
                  <th className="py-2 pr-3 font-medium">Code</th>
                  <th className="py-2 pr-3 font-medium">Emails</th>
                  <th className="py-2 font-medium"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {enrolments.map((e) => (
                  <tr key={e.id} className="border-b border-border align-top last:border-0">
                    <td className="py-2 pr-3">
                      <p className="font-medium">{e.name ?? 'No name'}</p>
                      <p className="text-xs text-muted-foreground">{e.email ?? `User ${e.userId}`}</p>
                    </td>
                    <td className="py-2 pr-3 whitespace-nowrap">{formatCalendarDate(e.triggerDate)}</td>
                    <td className="py-2 pr-3">
                      <div className="flex flex-wrap gap-1">
                        <Badge variant={STATUS_BADGE[e.status] ?? 'default'}>{statusLabel(e.status)}</Badge>
                        {e.isDryRun && <Badge variant="warning">Dry run</Badge>}
                      </div>
                      {e.stoppedReason && <p className="mt-1 text-xs text-muted-foreground">{e.stoppedReason}</p>}
                      {e.convertedAt && <p className="mt-1 text-xs text-muted-foreground">Paid {formatSeriesDate(e.convertedAt)}</p>}
                    </td>
                    <td className="py-2 pr-3">
                      {e.promoCode ? (
                        <>
                          <p className="font-mono text-xs">{e.promoCode}</p>
                          <p className="text-xs text-muted-foreground">until {formatSeriesDate(e.promoExpiresAt)}</p>
                        </>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {e.sends.length === 0 ? (
                        <span className="text-xs text-muted-foreground">None yet</span>
                      ) : (
                        <ul className="space-y-0.5 text-xs">
                          {e.sends.map((s) => <SendLine key={`${s.stepOrder}-${s.status}`} send={s} />)}
                        </ul>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      {canWrite && e.status === 'active' && (
                        <Button size="sm" variant="outline" onClick={() => setRemoving(e)}>Remove from series</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total.toLocaleString('en-AU')}
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</Button>
              <Button size="sm" variant="outline" disabled={offset + PAGE_SIZE >= total} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
            </div>
          </div>
        )}
      </CardContent>
      <ConfirmDialog
        open={removing !== null}
        title="Remove from series?"
        description={`${removing?.name ?? removing?.email ?? 'This customer'} won't be sent any more emails from this series, and their personal code stops working.`}
        confirmLabel="Remove"
        destructive
        busy={stop.isPending}
        onConfirm={confirmRemove}
        onOpenChange={(open) => !open && setRemoving(null)}
      />
    </Card>
  );
}
